import React, { useEffect, useMemo, useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import './AgentStatusPage.css';

// KPI 카드 컴포넌트
const AgentStatusKPICard: React.FC<{
  label: string;
  value: number;
  status?: 'total' | 'running' | 'error' | 'stopped';
}> = ({ label, value, status = 'total' }) => {
  return (
    <div className={`kpi-card kpi-card-${status}`}>
      <div className="kpi-label">{label}</div>
      <div className="kpi-value">{value}</div>
    </div>
  );
};

// KPI 섹션 컴포넌트
const AgentStatusKPISection: React.FC<{
  totalAgents: number;
  runningCount: number;
  errorCount: number;
  stoppedCount: number;
}> = ({ totalAgents, runningCount, errorCount, stoppedCount }) => {
  const { t } = useLanguage();

  const kpiStats = [
    { label: t('agentStatus.totalAgents'), value: totalAgents, status: 'total' as const },
    { label: t('agentStatus.running'), value: runningCount, status: 'running' as const },
    { label: t('agentStatus.error'), value: errorCount, status: 'error' as const },
    { label: t('agentStatus.stopped'), value: stoppedCount, status: 'stopped' as const },
  ];

  return (
    <section className="kpi-section">
      {kpiStats.map((stat, idx) => (
        <AgentStatusKPICard
          key={idx}
          label={stat.label}
          value={stat.value}
          status={stat.status}
        />
      ))}
    </section>
  );
};

interface KubeArmorAgent {
  id: string;
  nodeName: string;
  nodeIP: string;
  agentStatus: 'Running' | 'Stopped' | 'Error' | 'Unknown';
  agentVersion: string;
  kubeArmorVersion: string;
  osInfo: string;
  policies: number;
  appliedRules: number;
  recentLogs: string;
  lastHeartbeat: string;
  cpuUsage: number;
  memoryUsage: number;
}

const AgentStatusPage: React.FC = () => {
  const { t } = useLanguage();
  const [selectedAgent, setSelectedAgent] = useState<KubeArmorAgent | null>(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterVersion, setFilterVersion] = useState('all');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const agentData: KubeArmorAgent[] = useMemo(() => ([
    {
      id: 'agent-1',
      nodeName: 'worker-01',
      nodeIP: '10.0.1.10',
      agentStatus: 'Running',
      agentVersion: '0.10.2',
      kubeArmorVersion: '0.10.2',
      osInfo: 'Ubuntu 22.04 LTS (Linux 5.15.0)',
      policies: 12,
      appliedRules: 156,
      recentLogs: 'Policy enforcement active - 3 violations detected in last hour',
      lastHeartbeat: '2024-01-15 14:35:22',
      cpuUsage: 2.5,
      memoryUsage: 145,
    },
    {
      id: 'agent-2',
      nodeName: 'worker-02',
      nodeIP: '10.0.1.11',
      agentStatus: 'Running',
      agentVersion: '0.10.2',
      kubeArmorVersion: '0.10.2',
      osInfo: 'Ubuntu 22.04 LTS (Linux 5.15.0)',
      policies: 12,
      appliedRules: 156,
      recentLogs: 'All systems normal - No violations detected',
      lastHeartbeat: '2024-01-15 14:35:19',
      cpuUsage: 1.8,
      memoryUsage: 132,
    },
    {
      id: 'agent-3',
      nodeName: 'master-01',
      nodeIP: '10.0.1.5',
      agentStatus: 'Running',
      agentVersion: '0.10.2',
      kubeArmorVersion: '0.10.2',
      osInfo: 'Ubuntu 22.04 LTS (Linux 5.15.0)',
      policies: 15,
      appliedRules: 189,
      recentLogs: 'Control plane protection active - 1 violation blocked',
      lastHeartbeat: '2024-01-15 14:35:21',
      cpuUsage: 3.2,
      memoryUsage: 167,
    },
    {
      id: 'agent-4',
      nodeName: 'worker-03',
      nodeIP: '10.0.1.12',
      agentStatus: 'Error',
      agentVersion: '0.10.1',
      kubeArmorVersion: '0.10.2',
      osInfo: 'Ubuntu 20.04 LTS (Linux 5.10.0)',
      policies: 12,
      appliedRules: 0,
      recentLogs: 'ERROR: Connection timeout - Unable to reach KubeArmor daemon',
      lastHeartbeat: '2024-01-15 14:10:45',
      cpuUsage: 0,
      memoryUsage: 0,
    },
    {
      id: 'agent-5',
      nodeName: 'data-store-01',
      nodeIP: '10.0.1.20',
      agentStatus: 'Stopped',
      agentVersion: '0.9.5',
      kubeArmorVersion: '0.10.2',
      osInfo: 'CentOS 8 (Linux 4.18.0)',
      policies: 5,
      appliedRules: 0,
      recentLogs: 'Agent stopped - Last activity: 2 hours ago',
      lastHeartbeat: '2024-01-15 12:25:30',
      cpuUsage: 0,
      memoryUsage: 0,
    },
  ]), []);

  const filteredAgents = useMemo(() => {
    return agentData.filter(agent => {
      const statusMatch = filterStatus === 'all' || agent.agentStatus === filterStatus;
      const versionMatch = filterVersion === 'all' || agent.agentVersion === filterVersion;
      return statusMatch && versionMatch;
    });
  }, [filterStatus, filterVersion, agentData]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Running': return '#22c55e';
      case 'Stopped': return '#9ca3af';
      case 'Error': return '#ff4444';
      case 'Unknown': return '#ffd700';
      default: return '#666666';
    }
  };

  const getVersionMatch = (agentVersion: string, kubeArmorVersion: string) => {
    return agentVersion === kubeArmorVersion ? 'match' : 'mismatch';
  };

  const uniqueVersions = [...new Set(agentData.map(agent => agent.agentVersion))];

  return (
    <div className="agent-status-container">
      <div className="detail-header">
        <h1>{t('agentStatus.detailTitle')}</h1>
        <p>{t('agentStatus.detailDescription')}</p>
      </div>

      {/* KPI 카드 */}
      <AgentStatusKPISection
        totalAgents={agentData.length}
        runningCount={agentData.filter(a => a.agentStatus === 'Running').length}
        errorCount={agentData.filter(a => a.agentStatus === 'Error').length}
        stoppedCount={agentData.filter(a => a.agentStatus === 'Stopped').length}
      />

      {/* 필터 섹션 */}
      <section className="filter-section">
        <div className="filter-group">
          <label>{t('agentStatus.filters.status')}</label>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
            <option value="all">{t('agentStatus.filters.all')}</option>
            <option value="Running">Running</option>
            <option value="Stopped">Stopped</option>
            <option value="Error">Error</option>
          </select>
        </div>
        <div className="filter-group">
          <label>{t('agentStatus.filters.version')}</label>
          <select value={filterVersion} onChange={(e) => setFilterVersion(e.target.value)}>
            <option value="all">{t('agentStatus.filters.all')}</option>
            {uniqueVersions.map(v => (
              <option key={v} value={v}>{v}</option>
            ))}
          </select>
        </div>
        <div className="filter-stat">
          <span>{t('agentStatus.filters.searchResults')}: <strong>{filteredAgents.length}</strong></span>
        </div>
      </section>

      {/* 테이블 및 상세 정보 */}
      <div className="content-layout">
        {/* 데이터 테이블 */}
        <section className="table-section">
          <div className="table-header">
            <div className="col-node">{t('agentStatus.columns.nodeName')}</div>
            <div className="col-ip">{t('agentStatus.columns.nodeIP')}</div>
            <div className="col-status">{t('agentStatus.columns.agentStatus')}</div>
            <div className="col-version">{t('agentStatus.columns.agentVersion')}</div>
            <div className="col-heartbeat">{t('agentStatus.columns.lastHeartbeat')}</div>
            <div className="col-usage">{t('agentStatus.columns.resourceUsage')}</div>
          </div>
          <div className="table-body">
            {filteredAgents.map((agent) => (
              <div
                key={agent.id}
                className={`table-row ${selectedAgent?.id === agent.id ? 'active' : ''}`}
                onClick={() => setSelectedAgent(agent)}
              >
                <div className="col-node">{agent.nodeName}</div>
                <div className="col-ip">{agent.nodeIP}</div>
                <div className="col-status">
                  <span className="status-badge" style={{backgroundColor: `${getStatusColor(agent.agentStatus)}20`, color: getStatusColor(agent.agentStatus)}}>
                    {agent.agentStatus}
                  </span>
                </div>
                <div className="col-version">
                  <div className="version-info">
                    <div>{agent.agentVersion}</div>
                    {getVersionMatch(agent.agentVersion, agent.kubeArmorVersion) === 'mismatch' && (
                      <div className="version-mismatch">⚠️ Mismatch</div>
                    )}
                  </div>
                </div>
                <div className="col-heartbeat">{agent.lastHeartbeat}</div>
                <div className="col-usage">
                  <div className="usage-info">
                    <span>CPU: {agent.cpuUsage}%</span>
                    <span>MEM: {agent.memoryUsage}MB</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* 상세 정보 */}
        <section className="detail-section">
          {selectedAgent ? (
            <div className="detail-card">
              <div className="detail-title">에이전트 상세</div>
              <div className="detail-content">
                <div className="detail-row">
                  <div className="detail-label">노드명</div>
                  <div className="detail-value">{selectedAgent.nodeName}</div>
                </div>
                <div className="detail-row">
                  <div className="detail-label">노드 IP</div>
                  <div className="detail-value">{selectedAgent.nodeIP}</div>
                </div>
                <div className="detail-row">
                  <div className="detail-label">상태</div>
                  <div className="detail-value">
                    <span style={{color: getStatusColor(selectedAgent.agentStatus)}}>
                      {selectedAgent.agentStatus}
                    </span>
                  </div>
                </div>
                <div className="detail-row">
                  <div className="detail-label">에이전트 버전</div>
                  <div className="detail-value">{selectedAgent.agentVersion}</div>
                </div>
                <div className="detail-row">
                  <div className="detail-label">KubeArmor 버전</div>
                  <div className="detail-value">
                    {selectedAgent.kubeArmorVersion}
                    {getVersionMatch(selectedAgent.agentVersion, selectedAgent.kubeArmorVersion) === 'mismatch' && (
                      <span style={{color: '#ffd700', marginLeft: '8px'}}>⚠️ Version mismatch detected</span>
                    )}
                  </div>
                </div>
                <div className="detail-row">
                  <div className="detail-label">OS 정보</div>
                  <div className="detail-value">{selectedAgent.osInfo}</div>
                </div>
                <div className="detail-row">
                  <div className="detail-label">적용된 정책</div>
                  <div className="detail-value">{selectedAgent.policies}</div>
                </div>
                <div className="detail-row">
                  <div className="detail-label">적용된 규칙</div>
                  <div className="detail-value">{selectedAgent.appliedRules}</div>
                </div>
                <div className="detail-row">
                  <div className="detail-label">마지막 하트비트</div>
                  <div className="detail-value">{selectedAgent.lastHeartbeat}</div>
                </div>
                <div className="detail-row">
                  <div className="detail-label">CPU 사용량</div>
                  <div className="detail-value">
                    <div className="usage-bar">
                      <div className="usage-fill" style={{width: `${Math.min(selectedAgent.cpuUsage * 10, 100)}%`, backgroundColor: '#3b82f6'}} />
                    </div>
                    <span>{selectedAgent.cpuUsage}%</span>
                  </div>
                </div>
                <div className="detail-row">
                  <div className="detail-label">메모리 사용량</div>
                  <div className="detail-value">
                    <div className="usage-bar">
                      <div className="usage-fill" style={{width: `${Math.min((selectedAgent.memoryUsage / 256) * 100, 100)}%`, backgroundColor: '#8b5cf6'}} />
                    </div>
                    <span>{selectedAgent.memoryUsage} MB</span>
                  </div>
                </div>
                <div className="detail-row">
                  <div className="detail-label">최근 로그</div>
                  <div className="detail-value detail-description">{selectedAgent.recentLogs}</div>
                </div>
              </div>
            </div>
          ) : (
            <div className="detail-card empty-state">
              <div className="empty-message">
                {t('common.selectFromList')}
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default AgentStatusPage;
